import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { getMenuItemIcon, Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { ManageMembersType, ManageMembersView, MenuItemType } from '@nx-frontend/shared/types';
import { ManageMembersSeatsCount } from './manage-members-seats-count';
import { UpgradePlanMessage } from './messages/upgrade-plan-message';
import {
  $currentUserCanInvite,
  $isFetching,
  $manageMembersType,
  $menuItem,
  $showUpgradeMessage,
  $viewMode,
  updateViewModeEvent,
} from './store';

export function ManageMembersHeader({ onClose }: { onClose?: () => void }) {
  const t = useTranslations('features');
  const [manageMembersType, viewMode, showUpgradeMessage, isFetching, currentUserCanInvite] = useUnit([
    $manageMembersType,
    $viewMode,
    $showUpgradeMessage,
    $isFetching,
    $currentUserCanInvite,
  ]);
  const [updateViewMode] = useEvent([updateViewModeEvent]);

  const showNodeTitle = manageMembersType === ManageMembersType.PORTAL_NODE;

  const handleInviteClick = () => {
    if (manageMembersType === ManageMembersType.PORTAL_NODE) {
      if (currentUserCanInvite) {
        updateViewMode(ManageMembersView.INVITE);
      } else {
        updateViewMode(ManageMembersView.ORG_ACCESS_LIST);
      }
    }
  };

  return (
    <div>
      <div className="flex items-center">
        <ManageMembersTitle />
        {showNodeTitle && (
          <div className="ml-[6px]">
            <ManageMembersNodeTitle />
          </div>
        )}
        {onClose && (
          <div onClick={onClose} className="cursor-pointer ml-auto">
            <Icon icon={IconsMap.CLOSE} className="w-4 h-4 text-neutral-500" />
          </div>
        )}
      </div>

      {!isFetching && (
        <div className={classNames(' flex items-center')}>
          <div className="flex flex-col">
            {viewMode !== ManageMembersView.NODE_ACCESS && (
              <div className="mt-1">
                <ManageMembersSeatsCount />
              </div>
            )}
          </div>

          <div className="ml-auto">
            {viewMode === ManageMembersView.NODE_ACCESS && (
              <Button
                className="mt-4 relative z-10"
                type={ButtonType.FLAT}
                size={ButtonSize.MD}
                onClick={handleInviteClick}
              >
                <Icon icon={IconsMap.USER_PLUS} className="w-5 h-5 text-white" />
                <span className="text-xs ml-2">{t('manage-members.invite')}</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {showUpgradeMessage && !isFetching && (
        <div className="mt-3">
          <UpgradePlanMessage />
        </div>
      )}
    </div>
  );
}

function ManageMembersNodeTitle() {
  const [menuItem] = useUnit([$menuItem]);
  const title = menuItem?.name ?? '';
  const icon = menuItem?.icon ? getMenuItemIcon({ type: menuItem.type, icon: menuItem.icon }) : undefined;

  return (
    <div className="flex flex-1 items-center min-w-0">
      {icon && <Icon icon={icon} className="w-5 h-5 text-neutral-800 mr-1" />}
      <div className="font-semibold flex flex-col flex-1 min-w-0 pr-10">
        {title && <div className="text-lg whitespace-nowrap overflow-hidden text-ellipsis ml-[2px]">{title}</div>}
      </div>
    </div>
  );
}

function ManageMembersTitle() {
  const t = useTranslations('features');
  const [menuItem, manageMembersType, viewMode] = useUnit([$menuItem, $manageMembersType, $viewMode]);

  const getModalTitle = () => {
    if (menuItem && manageMembersType === ManageMembersType.PORTAL_NODE) {
      if (viewMode === ManageMembersView.NODE_ACCESS) {
        if (
          menuItem.type === MenuItemType.PORTAL_SECTION ||
          menuItem.type === MenuItemType.NOTES_FOLDER ||
          menuItem.type === MenuItemType.FOLDERS
        ) {
          return t('manage-members.folders_access');
        } else if (menuItem.type === MenuItemType.LINK) {
          return t('manage-members.links_access');
        } else {
          return t('manage-members.pages_access');
        }
      } else {
        if (
          menuItem.type === MenuItemType.PORTAL_SECTION ||
          menuItem.type === MenuItemType.NOTES_FOLDER ||
          menuItem.type === MenuItemType.FOLDERS
        ) {
          return t('manage-members.add_member_to_folder');
        } else if (menuItem.type === MenuItemType.LINK) {
          return t('manage-members.add_member_to_link');
        } else {
          return t('manage-members.add_member_to_page');
        }
      }
    } else if (manageMembersType === ManageMembersType.PORTAL) {
      switch (viewMode) {
        case ManageMembersView.INVITE:
        case ManageMembersView.ORG_ACCESS_LIST:
          return t('manage-members.invite_to_portal');
        case ManageMembersView.NODE_ACCESS:
          return 'Portal’s access';
      }
    } else if (manageMembersType === ManageMembersType.WORKSPACE) {
      return 'Invite to workspace';
    }
  };
  return <div className="text-lg font-bold">{getModalTitle()}</div>;
}

export default ManageMembersHeader;
